import { useState, useCallback, useEffect, useRef } from "react";
import Debounce from "../utils/debounce";

/**
 * Custom hook for handling paginated data in Select component with infinite scroll
 * @param {Function} fetchFunction - Async function that takes pageNumber and pageSize and returns paginated results
 * @param {number} pageSize - Number of items per page (default: 10)
 * @returns {Object} - Contains options, isLoading, hasMore, and onPopupScroll handler
 */
export const useInterviewPaginatedSelect = (fetchFunction, pageSize = 10, status) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState("");
  const isInitialLoadRef = useRef(true);

  /**
   * Fetches a page of data
   * @param {number} pageNumber )
   */
  const fetchPage = useCallback(
    async (pageNumber, search = "") => {
      setIsLoading(true);
      try {
        const result = status !== undefined ? await fetchFunction(status, pageNumber, pageSize, search) : await fetchFunction(pageNumber, pageSize, search);

        if (pageNumber === 1) {
          setOptions(result.data || []);
        } else {
          setOptions((prevOptions) => [...prevOptions, ...(result.data || [])]);
        }
        const totalPages = result.totalPages;
        const current = result.currentPage;

        setHasMore(current < totalPages);
        setCurrentPage(current);
      } catch (error) {
        console.error("Error fetching paginated data:", error);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, fetchFunction, status],
  );

  const debouncer = new Debounce();
  const onSearch = (value) => {
    setSearchText(value);

    debouncer.debounce({
      debounce: () => {
        fetchPage(1, value);
      },
      debounceTimeout: 800,
    });
  };
  /**
   * Scroll event handler for Select popup
   * Triggers fetch when user scrolls near bottom
   */
  const handleScroll = useCallback(
    (event) => {
      const { target } = event;
      const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10;

      if (isNearBottom && hasMore && !isLoading) {
        fetchPage(currentPage + 1, searchText);
      }
    },
    [currentPage, hasMore, isLoading, fetchPage, searchText],
  );

  useEffect(() => {
    if (isInitialLoadRef.current) {
      fetchPage(1);
      isInitialLoadRef.current = false;
    }
  }, [fetchPage]);
  /**
   * Reset pagination to initial state
   * Useful when closing the dropdown or filtering
   */
  const reset = useCallback(() => {
    setOptions([]);
    setCurrentPage(1);
    setHasMore(true);
    isInitialLoadRef.current = true;
  }, []);

  /**
   * Manually refetch first page
   * Useful for refresh functionality
   */
  const refetch = useCallback(() => {
    isInitialLoadRef.current = false;
    fetchPage(1);
  }, [fetchPage]);

  return {
    options,
    isLoading,
    hasMore,
    onSearch: onSearch,
    onPopupScroll: handleScroll,
    reset,
    refetch,
  };
};
